// @snake-case-api Terraform module variables use snake_case names.
import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Alert, Badge, Button, Input, Spinner,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogBody, DialogFooter,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@agion/ui'
import { AlertTriangle, Key, Play } from 'lucide-react'
import type { CloudEnvironment } from '@/core/infra_types'
import { createOperation } from '@/features/operation/infra_operation.service'
import { generateBootstrapToken, regenerateHostToken } from './infra_governance.service'
import { listTerraformModules, getModuleVersions } from '@/features/terraform/infra_terraform.service'
import type { TerraformModule } from '@/features/terraform/infra_terraform.service'
import { listSshKeys } from '@/features/secondary/infra_ssh_key.service'
import type { SshKey } from '@/features/secondary/infra_ssh_key.service'
import { listImages } from '@/features/image/infra_image.service'
import { aptChannelFromVersion, channelForEnvironment } from '@/core/infra_utils'
import { getConfig } from '@/core/infra_runtime_env'
import type { PackerImage } from '@/features/image/infra_image.service'
import { AdvancedSettings } from './AdvancedSettings'
import { AzureSshAccess } from './AzureSshAccess'
import { appliedDiskSizeGb, azureResourceGroupName, diskSizeBounds, validSshAccess, type SshAccessMode } from './vmDeployment'
import { parseSshAllowedCidrs } from './advancedSettingsUtils'
import { azureNetworkVariables, readAzureNetwork, validAzureNetwork } from './vmNetwork'

type ImageChannel = 'stable' | 'unstable' | 'beta' | 'testing'

interface Props {
  open: boolean
  environment: CloudEnvironment
  onClose: () => void
  onSuccess: (operationId: string) => void
}

/** Derive terraform module name from environment's cloud_provider + deployment_model */
function deriveModuleName(env: CloudEnvironment): string {
  if (env.deploymentModel === 'k3s_bare') return 'k3s-cluster'
  // On-prem kubernetes (cloud_provider=none) uses k3s-cluster, not cloud GKE
  if (env.deploymentModel === 'k8s_edge' && env.cloudProvider === 'none') return 'k3s-cluster'
  if (env.deploymentModel === 'k8s_edge') return `${env.cloudProvider}-gke`
  return `${env.cloudProvider}-vm`
}

/** Derive image channel from packer image name */
function deriveImageChannel(imageName: string): ImageChannel {
  for (const ch of ['unstable', 'beta', 'testing'] as const) {
    if (imageName.includes(`-${ch}-`)) return ch
  }
  return 'stable'
}

function reapplyVmSize(provider: CloudEnvironment['cloudProvider'], raw: unknown): string {
  const value = raw == null ? '' : String(raw).trim()
  if (provider === 'azure') {
    // The fallback mirrors the azure-vm module default so a config with no
    // usable size re-applies to the same VM the module would provision if
    // vm_size were omitted entirely.
    return value.startsWith('Standard_') ? value : 'Standard_D2s_v5'
  }
  if (provider === 'upcloud') {
    return value || '2xCPU-4GB'
  }
  return value || 'e2-standard-4'
}

function recordFromUnknown(value: unknown): Record<string, unknown> {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>
  }
  return {}
}

const channelLabels: Record<ImageChannel, string> = {
  stable: 'Stable',
  unstable: 'Dev',
  beta: 'Beta',
  testing: 'Testing',
}

const channelVariants: Record<ImageChannel, 'success' | 'warning' | 'info' | 'default'> = {
  stable: 'success',
  unstable: 'warning',
  beta: 'info',
  testing: 'default',
}

/** Group images by channel, sorted by version descending within each group */
function groupImagesByChannel(images: PackerImage[]): Record<ImageChannel, PackerImage[]> {
  const groups: Record<ImageChannel, PackerImage[]> = {
    stable: [],
    unstable: [],
    beta: [],
    testing: [],
  }
  for (const img of images) {
    const ch = deriveImageChannel(img.name)
    groups[ch].push(img)
  }
  return groups
}

/** DC/HA compatibility editor. Single VMs always use SingleVmWizard. */
export function ClusterTerraformDialog({ open, environment, onClose, onSuccess }: Props) {
  const { t } = useTranslation(['pages'])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [terraformModules, setTerraformModules] = useState<TerraformModule[]>([])
  const [selectedVersion, setSelectedVersion] = useState<string>('')
  const [isLoadingModules, setIsLoadingModules] = useState(false)
  const [packerImages, setPackerImages] = useState<PackerImage[]>([])
  const [selectedImageName, setSelectedImageName] = useState<string>('')
  const [sudoPassword, setSudoPassword] = useState<string>('')
  const [sshKeys, setSshKeys] = useState<SshKey[]>([])
  const [selectedSshKeyId, setSelectedSshKeyId] = useState<string>('')

  // Advanced settings: kept collapsed by default (progressive disclosure).
  // Empty string for zone/diskSizeGb means "use module default" — only
  // forwarded as tfvars when the user explicitly overrides.
  const [advancedOpen, setAdvancedOpen] = useState(false)
  // GCP-only zone (region-anchored like "europe-north1-b").
  const [zone, setZone] = useState<string>('')
  // Shared with Azure.
  const [diskSizeGb, setDiskSizeGb] = useState<string>('')
  const [dfsEnabled, setDfsEnabled] = useState<boolean>(false)
  // Azure-only advanced fields.
  const [availabilityZones, setAvailabilityZones] = useState<string[]>([])
  const [dfsReplication, setDfsReplication] = useState<string>('LRS')
  const [diagnosticsEnabled, setDiagnosticsEnabled] = useState<boolean>(true)
  const [logRetentionDays, setLogRetentionDays] = useState<string>('30')
  const [sshAllowedCidrs, setSshAllowedCidrs] = useState<string>('*')
  const [sshAccessMode, setSshAccessMode] = useState<SshAccessMode>('legacy')

  const moduleName = deriveModuleName(environment)
  const versions = getModuleVersions(terraformModules, moduleName)

  const cfg = (environment.config ?? {}) as Record<string, unknown>
  const lastApplyVariables = recordFromUnknown(cfg['lastApplyVariables'] ?? cfg['last_apply_variables'])
  const recordedVersion = String(lastApplyVariables['moduleVersion'] ?? lastApplyVariables['module_version'] ?? cfg['moduleVersion'] ?? cfg['module_version'] ?? cfg['tfModuleVersion'] ?? cfg['tf_module_version'] ?? '')
  const recordedDigest = String(lastApplyVariables['moduleDigest'] ?? lastApplyVariables['module_digest'] ?? cfg['moduleDigest'] ?? cfg['module_digest'] ?? '')
  // An ordinary reapply is not an implicit upgrade.
  useEffect(() => {
    if (!selectedVersion) setSelectedVersion(recordedVersion || versions[0] || '')
  }, [recordedVersion, versions, selectedVersion])
  const replicaCount = Number(cfg['replicaCount'] ?? cfg['replica_count'] ?? 1)
  const isHA = replicaCount > 1
  // GET /environments strips last_apply_variables; vm_single environments
  // carry the applied variables as vmApplyConfiguration instead.
  const applyConfiguration = recordFromUnknown(cfg['vmApplyConfiguration'] ?? cfg['vm_apply_configuration'])
  const currentImageName = String(
    cfg['currentImage']
      ?? cfg['current_image']
      ?? applyConfiguration['templateName']
      ?? applyConfiguration['template_name']
      ?? applyConfiguration['imageName']
      ?? applyConfiguration['image_name']
      ?? lastApplyVariables['image_name']
      ?? lastApplyVariables['template_name']
      ?? '',
  )

  const imageGroups = useMemo(() => groupImagesByChannel(packerImages), [packerImages])
  const channelsWithImages = useMemo(
    () => (Object.keys(imageGroups) as ImageChannel[]).filter(ch => imageGroups[ch].length > 0),
    [imageGroups],
  )

  useEffect(() => {
    if (open) {
      setError(null)
      setSelectedVersion(recordedVersion)
      setSelectedImageName('')
      setSudoPassword('')
      const envCfg = (environment.config ?? {}) as Record<string, unknown>
      setSelectedSshKeyId(String(envCfg['sshKeyId'] ?? envCfg['ssh_key_id'] ?? ''))
      // Advanced settings rehydrate from env.Config (both camelCase
      // written by the post-apply writeback and snake_case written by the
      // wizard on create). Falling back to empty/false keeps re-applies
      // of pre-existing envs non-surprising: a fresh DFS bucket is never
      // provisioned unless the user explicitly opted in.
      setAdvancedOpen(false)
      setZone(String(envCfg['zone'] ?? ''))
      const diskCfg = envCfg['diskSizeGb'] ?? envCfg['disk_size_gb']
      setDiskSizeGb(diskCfg != null ? String(diskCfg) : '')
      // Explicit boolean coercion — Boolean('false') is true in JS,
      // which would silently flip the checkbox the wrong way when the
      // backend writeback stores the string "false".
      const dfsRaw = envCfg['dfsEnabled'] ?? envCfg['dfs_enabled']
      setDfsEnabled(dfsRaw === true || dfsRaw === 'true')
      // Azure-only rehydration. availabilityZones may have been stored as
      // a real list by the Go writeback OR a snake_case list by the wizard
      // createEnvironment write; either shape lands as Array<string>.
      // Unknown shape (legacy env, foreign string) falls back to [].
      const azRaw = envCfg['availabilityZones'] ?? envCfg['availability_zones']
      setAvailabilityZones(Array.isArray(azRaw) ? azRaw.filter((z): z is string => typeof z === 'string') : [])
      setDfsReplication(String(envCfg['dfsReplication'] ?? envCfg['dfs_replication'] ?? 'LRS'))
      const diagRaw = envCfg['diagnosticsEnabled'] ?? envCfg['diagnostics_enabled']
      // Diagnostics defaults to true when the env has never been applied with
      // an explicit value — module default is true, so rehydrating as false
      // would misrepresent state. Only treat an explicit literal false/'false'
      // as off.
      setDiagnosticsEnabled(diagRaw === false || diagRaw === 'false' ? false : true)
      const logRetRaw = envCfg['logRetentionDays'] ?? envCfg['log_retention_days']
      setLogRetentionDays(logRetRaw != null && logRetRaw !== '' ? String(logRetRaw) : '30')
      // sshAllowedCidrs is stored as string[] by the writeback + wizard;
      // the UI input field is comma-separated text so we join on rehydrate.
      const snapshot = recordFromUnknown(envCfg['lastApplyVariables'] ?? envCfg['last_apply_variables'])
      setSshAccessMode(String(snapshot['ssh_access_mode'] ?? snapshot['sshAccessMode'] ?? envCfg['sshAccessMode'] ?? envCfg['ssh_access_mode'] ?? 'legacy') as SshAccessMode)
      let cidrRaw = envCfg['sshAllowedCidrs'] ?? envCfg['ssh_allowed_cidrs']
      const savedSources = snapshot['ssh_allowed_cidrs_json'] ?? snapshot['sshAllowedCidrsJson']
      if (typeof savedSources === 'string') {
        try { cidrRaw = JSON.parse(savedSources) } catch { /* Existing config remains the fallback. */ }
      }
      const cidrList = Array.isArray(cidrRaw)
        ? cidrRaw.filter((c): c is string => typeof c === 'string')
        : typeof cidrRaw === 'string' ? parseSshAllowedCidrs(cidrRaw) : ['*']
      setSshAllowedCidrs(cidrList.length > 0 ? cidrList.join(', ') : '*')
      setIsLoadingModules(true)
      Promise.all([
        listTerraformModules().then(setTerraformModules),
        listImages({ status: 'available', cloudProvider: environment.cloudProvider })
          .then(resp => {
            const envChannel = channelForEnvironment(getConfig('VITE_ENVIRONMENT'))
            const all = resp.data ?? []
            setPackerImages(envChannel ? all.filter(img => aptChannelFromVersion(img.version) === envChannel) : all)
          }),
        listSshKeys().then(resp => setSshKeys(resp.data ?? [])),
      ])
        .catch(() => setError('Failed to load modules or images'))
        .finally(() => setIsLoadingModules(false))
    }
  }, [open, environment.cloudProvider, environment.config, recordedVersion])

  const isK3s = moduleName === 'k3s-cluster'
  const isUpCloud = environment.cloudProvider === 'upcloud'
  // gcp-gke resolves node images itself and has no disk_size_gb variable, so
  // the single-VM image and disk rules apply to gcp-vm only.
  const isGcpVm = moduleName === 'gcp-vm'
  const isAzureVm = moduleName === 'azure-vm'

  const handleSubmit = async () => {
    setIsSubmitting(true)
    setError(null)
    try {
      if (moduleName === 'azure-vm' && !validSshAccess(sshAccessMode, sshAllowedCidrs)) {
        throw new Error(t('pages:sshAccess.sourcesHint'))
      }
      // Without image_name the gcp-vm module resolves the newest image in the
      // channel family, and a new image is ForceNew on the instance: the
      // apply would destroy the VM and its disk. Resend the recorded image.
      if (isGcpVm && !(selectedImageName || currentImageName).trim()) {
        throw new Error(t('pages:environments.applyTerraform.imageRequiredGcp', 'Choose the image this virtual machine runs. Its current image is not recorded, and applying without one resolves the newest image in the channel, which destroys the virtual machine and its disk.'))
      }
      // azure-vm puts the first of its SSH keys in admin_ssh_key, which is
      // ForceNew on azurerm_linux_virtual_machine: an apply that cannot
      // resupply the same key destroys the VM, its extension and its data
      // collection rule association. infra-service resolves the key from the
      // recorded ssh_key_id, so an id that is missing or points at a deleted
      // key has to stop the apply here.
      if (isAzureVm && !sshKeys.some(key => key.id === selectedSshKeyId)) {
        throw new Error(t('pages:environments.applyTerraform.sshKeyRequiredAzure', 'The SSH key of this virtual machine cannot be resolved, and applying without it destroys the virtual machine and everything on its disk. Restore the SSH key this machine was built with under Security, SSH keys, then re-apply.'))
      }
      if (isUpCloud) {
        // An empty template_name matches the newest template in the account,
        // and a different template rebuilds the server.
        if (!(selectedImageName || currentImageName).trim()) {
          throw new Error(t('pages:environments.applyTerraform.imageRequiredUpcloud', 'Choose the image this server runs. Its current image is not recorded, and applying without one could rebuild the server on a different image.'))
        }
        const bounds = diskSizeBounds('upcloud')
        const appliedDisk = appliedDiskSizeGb(environment)
        if (diskSizeGb && (!/^\d+$/.test(diskSizeGb) || Number(diskSizeGb) < bounds.min || Number(diskSizeGb) > bounds.max)) {
          throw new Error(t('pages:vmWizard.requirements.storage', { defaultValue: 'Set the boot disk size to {{min}}–{{max}} GB in Advanced settings, or leave it empty for the default.', ...bounds }))
        }
        if (diskSizeGb && appliedDisk !== undefined && Number(diskSizeGb) < appliedDisk) {
          throw new Error(t('pages:vmWizard.requirements.diskShrink', { defaultValue: 'UpCloud disks cannot shrink. Set the boot disk to at least {{size}} GB in Advanced settings, or leave it empty to keep the current disk.', size: appliedDisk }))
        }
      }
      let variables: Record<string, string>

      if (isK3s) {
        // k3s-cluster module: bastion SSH through jump host to on-prem nodes
        const bastionIp = String(cfg['bastionIp'] ?? cfg['bastion_ip'] ?? '')
        const sshUser = String(cfg['sshUser'] ?? cfg['ssh_user'] ?? 'agion')
        const clusterMode = String(cfg['clusterMode'] ?? cfg['cluster_mode'] ?? 'ha')
        const nodes = cfg['nodes'] as Array<{ ip: string; labels: Record<string, string> }> | undefined

        if (!bastionIp) throw new Error('Environment config missing bastion_ip')
        if (!nodes || nodes.length === 0) throw new Error('Environment config missing nodes')

        // Resolve SSH key vault path from selected key or fall back to environment config
        const selectedKey = sshKeys.find(k => k.id === selectedSshKeyId)
        const sshKeyVaultPath = selectedKey?.vaultPath
          ?? (selectedSshKeyId ? `secret/data/agion/credentials/ssh-key-${selectedSshKeyId}` : '')

        variables = {
          bastion_ip: bastionIp,
          bastion_user: sshUser,
          nodes_json: JSON.stringify(nodes),
          ssh_user: sshUser,
          environment_name: environment.name,
          cluster_mode: clusterMode,
          ...(sshKeyVaultPath ? { ssh_key_vault_path: sshKeyVaultPath } : {}),
          ...(selectedVersion ? { module_version: selectedVersion } : {}),
          ...(sudoPassword ? { sudo_password: sudoPassword } : {}),
        }
      } else {
        // Cloud modules: VM/GKE with cloud provider credentials
        const managedDbEnabled = cfg['managedDbEnabled'] ?? cfg['managed_db_enabled'] ?? false
        const separateIntelligenceDb = cfg['separateIntelligenceDb'] ?? cfg['separate_intelligence_db'] ?? false
        const vmSize = reapplyVmSize(environment.cloudProvider, cfg['vmSize'] ?? cfg['vm_size'])
        // disk_size_gb defaults to 50 in every single-VM module, so an empty
        // field is a request for 50 GB rather than for the current disk.
        // Resend what the last apply recorded. upcloud-vm is left alone: its
        // apply worker reads the live disk and fills the variable in. gcp-gke
        // declares no disk_size_gb at all, so only the single-VM modules
        // receive the fallback.
        const recordedDisk = appliedDiskSizeGb(environment)
        const diskToApply = diskSizeGb || (recordedDisk !== undefined && (isGcpVm || isAzureVm) ? String(recordedDisk) : '')
        variables = {
          environment_name: environment.name,
          vm_size: vmSize,
          replica_count: String(replicaCount),
          managed_db_enabled: String(managedDbEnabled),
          separate_intelligence_db: String(separateIntelligenceDb),
          ...(selectedVersion ? { module_version: selectedVersion } : {}),
          ...(selectedImageName && environment.cloudProvider !== 'upcloud' ? { image_name: selectedImageName } : {}),
        }

        // Provider-specific variables
        if (environment.cloudProvider === 'gcp') {
          variables['region'] = environment.region
          variables['network_cidr'] = String(cfg['networkCidr'] ?? cfg['network_cidr'] ?? '10.0.0.0/16')
          // Fallback is replicaCount-aware because env.Config lacks haMode
          // until buildApplyConfigWriteback runs after a successful apply.
          // If the very first apply fails (e.g. module_version gate rejects
          // the request), the re-apply reaches this branch with no haMode
          // and must infer the correct mode from the replica count, not
          // assume HA. See docs/rca/2026-04-20-gcp-byoh-ha-mode-rehydration.md.
          variables['ha_mode'] = String(
            cfg['haMode'] ?? cfg['ha_mode'] ?? (replicaCount === 1 ? 'standalone' : 'active_active'),
          )
          variables['bastion_enabled'] = String(cfg['bastionEnabled'] ?? cfg['bastion_enabled'] ?? 'true')
          if (managedDbEnabled) variables['db_tier'] = String(cfg['dbTier'] ?? cfg['db_tier'] ?? 'db-custom-2-8192')
          if (managedDbEnabled && separateIntelligenceDb) variables['intelligence_db_tier'] = String(cfg['intelligenceDbTier'] ?? cfg['intelligence_db_tier'] ?? 'db-custom-2-8192')
          // Advanced settings: only forward overrides. Zone and disk size
          // are omitted when empty so the module keeps its declared
          // defaults; dfs_enabled is always explicit because its state
          // directly gates resource creation (GCS bucket, SA, IAM).
          if (zone) variables['zone'] = zone
          if (diskToApply) variables['disk_size_gb'] = diskToApply
          // Resend the recorded image when the operator picked none, the way
          // the UpCloud path resends template_name. image_channel below only
          // feeds the family lookup, which the module skips once image_name
          // is set.
          if (isGcpVm) variables['image_name'] = selectedImageName || currentImageName
          variables['dfs_enabled'] = String(dfsEnabled)
          // Pin image_channel to the env's channel so terraform's family
          // lookup resolves to agion-edge-{unstable,beta,stable,testing}
          // — matching what packer writes. Without this, the module falls
          // back to default "stable" and the dev env pulls the ancient
          // legacy `agion-edge` family (Mar 12) instead of today's
          // `agion-edge-unstable`. See docs/rca/2026-04-21-gcp-packer-channel-asymmetry.md.
          // Only GCP uses image_channel; Azure/UpCloud resolve images differently.
          const envChannel = channelForEnvironment(getConfig('VITE_ENVIRONMENT'))
          if (envChannel) variables['image_channel'] = envChannel
        } else if (environment.cloudProvider === 'azure') {
          const network = readAzureNetwork(cfg)
          if (!validAzureNetwork(network)) throw new Error('Stored Azure network allocation is invalid; reconcile all subnets before reapplying')
          Object.assign(variables, azureNetworkVariables(network))
          variables['location'] = environment.region
          variables['resource_group_name'] = azureResourceGroupName(String(cfg['resourceGroupName'] ?? cfg['resource_group_name'] ?? ''), environment.name)
          if (managedDbEnabled) variables['db_sku'] = String(cfg['dbTier'] ?? cfg['db_tier'] ?? cfg['dbSku'] ?? cfg['db_sku'] ?? 'B_Standard_B2s')
          if (managedDbEnabled && separateIntelligenceDb) variables['intelligence_db_sku'] = String(cfg['intelligenceDbTier'] ?? cfg['intelligence_db_tier'] ?? cfg['intelligenceDbSku'] ?? cfg['intelligence_db_sku'] ?? 'B_Standard_B2s')
          // Track A3 advanced fields — always forwarded so re-apply and
          // decommission replay are deterministic. List-shaped vars use
          // the `_json` transport convention (extractJSONVariables
          // rewrites them to typed list(string) HCL vars).
          if (diskToApply) variables['disk_size_gb'] = diskToApply
          variables['availability_zones_json'] = JSON.stringify(availabilityZones)
          variables['ssh_access_mode'] = sshAccessMode
          variables['ssh_allowed_cidrs_json'] = JSON.stringify(sshAccessMode === 'vpn_only' ? [] : parseSshAllowedCidrs(sshAllowedCidrs))
          variables['dfs_enabled'] = String(dfsEnabled)
          variables['dfs_replication'] = dfsReplication
          variables['diagnostics_enabled'] = String(diagnosticsEnabled)
          variables['log_retention_days'] = logRetentionDays
          if (diskSizeGb) variables['disk_size_gb'] = diskSizeGb
        } else if (environment.cloudProvider === 'upcloud') {
          variables['zone'] = environment.region
          variables['plan'] = vmSize
          variables['template_name'] = selectedImageName || currentImageName
          if (diskSizeGb) variables['disk_size_gb'] = diskSizeGb
          variables['dfs_enabled'] = String(dfsEnabled)
          if (managedDbEnabled) variables['db_plan'] = String(cfg['dbTier'] ?? cfg['db_tier'] ?? cfg['dbPlan'] ?? cfg['db_plan'] ?? '1x2xCPU-4GB-50GB')
          if (managedDbEnabled && separateIntelligenceDb) variables['intelligence_db_plan'] = String(cfg['intelligenceDbTier'] ?? cfg['intelligence_db_tier'] ?? cfg['intelligenceDbPlan'] ?? cfg['intelligence_db_plan'] ?? '1x2xCPU-4GB-50GB')
        }

        // Generate bootstrap config from host group or single host
        const hostGroupId = (cfg['hostGroupId'] ?? cfg['host_group_id']) as string | undefined
        const hostId = (cfg['hostId'] ?? cfg['host_id']) as string | undefined

        if (hostGroupId) {
          const bootstrap = await generateBootstrapToken(hostGroupId)
          if (!bootstrap.bootstrapConfig && !bootstrap.bootstrapToken) {
            throw new Error('Failed to generate bootstrap config for host group')
          }
          variables['bootstrap_config'] = bootstrap.bootstrapConfig ?? bootstrap.bootstrapToken
        } else if (hostId) {
          const bootstrap = await regenerateHostToken(hostId)
          if (!bootstrap.bootstrapConfig && !bootstrap.bootstrapToken) {
            throw new Error('Failed to generate bootstrap config for host')
          }
          variables['bootstrap_config'] = bootstrap.bootstrapConfig ?? bootstrap.bootstrapToken
        }
        // No host group or host — bootstrap_config will be empty (re-apply on existing VM)
      }

      const digest = selectedVersion === recordedVersion && recordedDigest
        ? recordedDigest : terraformModules.find(m => m.module === moduleName && m.tags.includes(selectedVersion))?.digest
      if (digest) variables['module_digest'] = digest
      const op = await createOperation({
        environmentId: environment.id,
        operationType: 'terraform_apply',
        parameters: {
          module: moduleName,
          variables,
        },
      })
      onSuccess(op.id)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create operation')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent size="xl" maxHeight="90vh" className="w-[calc(100%-2rem)] max-h-[90dvh]">
        <DialogHeader className="shrink-0 pr-6">
          <DialogTitle>
            {isHA
              ? t('pages:environments.applyTerraform.titleRolling', 'Update Image (Rolling)')
              : t('pages:environments.applyTerraform.title', 'Apply Terraform')}
          </DialogTitle>
          <DialogDescription>
            {isHA
              ? t('pages:environments.applyTerraform.descriptionRolling',
                  'Replaces VMs one at a time to avoid cluster downtime. {{count}} replicas will be updated sequentially.',
                  { count: replicaCount })
              : t('pages:environments.applyTerraform.description',
                  'Run terraform apply on this environment with the selected module version.')}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="min-w-0 overscroll-contain px-1 py-4">
          <div className="grid grid-cols-2 gap-2 text-sm">
            <span className="text-slate-500">{t('pages:environments.applyTerraform.module', 'Module')}</span>
            <span className="font-mono font-medium">{moduleName}</span>
            <span className="text-slate-500">{t('pages:environments.detail.provider', 'Cloud Provider')}</span>
            <span className="font-medium">{environment.cloudProvider === 'none' ? t('pages:providers.onPremise') : environment.cloudProvider.toUpperCase()}</span>
            {isHA && (
              <>
                <span className="text-slate-500">{t('pages:environments.applyTerraform.replicas', 'Replicas')}</span>
                <span className="font-medium">{replicaCount}</span>
              </>
            )}
          </div>

          {/* Current image */}
          {currentImageName && (
            <div className="rounded-md bg-slate-50 dark:bg-slate-800 p-3">
              <p className="text-xs text-slate-500 mb-1">
                {t('pages:environments.applyTerraform.currentImage', 'Current Image')}
              </p>
              <p className="text-sm font-mono text-slate-700 dark:text-slate-300 truncate">
                {currentImageName}
              </p>
            </div>
          )}

          {/* Rolling update notice */}
          {isHA && (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-950 p-3">
              <AlertTriangle size={16} className="text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <p className="text-xs text-amber-700 dark:text-amber-300">
                {t('pages:environments.applyTerraform.rollingNotice',
                  'Rolling update: each replica will be destroyed and recreated one at a time with a 60s wait between. Total time ~{{minutes}} min.',
                  { minutes: Math.ceil(replicaCount * 3.5) })}
              </p>
            </div>
          )}

          {isLoadingModules ? (
            <div className="flex justify-center py-2"><Spinner className="h-4 w-4" /></div>
          ) : versions.length > 0 || recordedVersion ? (
            <div className="space-y-1">
              <p className="text-xs text-slate-500">{t('pages:environments.applyTerraform.version', 'Module Version')}</p>
              <Select value={selectedVersion} onValueChange={setSelectedVersion}>
                <SelectTrigger>
                  <SelectValue placeholder={t('pages:environments.applyTerraform.latestVersion', 'Latest')} />
                </SelectTrigger>
                <SelectContent>
                  {recordedVersion && !versions.includes(recordedVersion) && <SelectItem value={recordedVersion}>{recordedVersion}</SelectItem>}
                  {versions.map(v => (
                    <SelectItem key={v} value={v}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <p className="text-xs text-slate-500">
              {t('pages:vmWizard.noRelease', 'No pinned releases are available for this provider. Configure the registry connection or publish a release before provisioning.')}
            </p>
          )}

          {/* SSH Key selector for k3s/on-prem environments */}
          {isK3s && sshKeys.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs text-slate-500 flex items-center gap-1">
                <Key size={12} />
                {t('pages:environments.applyTerraform.sshKey', 'SSH Key')}
              </p>
              <Select value={selectedSshKeyId} onValueChange={setSelectedSshKeyId}>
                <SelectTrigger>
                  <SelectValue placeholder={t('pages:environments.applyTerraform.sshKeySelect', 'Select SSH key...')} />
                </SelectTrigger>
                <SelectContent>
                  {sshKeys.map(key => (
                    <SelectItem key={key.id} value={key.id}>
                      {/* eslint-disable-next-line i18next/no-literal-string -- dynamic API data */}
                      {key.name} ({key.keyType}) — {key.fingerprint.substring(0, 20)}...
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Sudo password for on-prem k3s nodes */}
          {isK3s && !!(cfg['sudoPasswordSet'] ?? cfg['sudo_password_set']) && (
            <div className="space-y-1">
              <p className="text-xs text-slate-500">{t('pages:environments.applyTerraform.sudoPassword')}</p>
              <Input
                type="password"
                value={sudoPassword}
                onChange={(e) => setSudoPassword(e.target.value)}
                placeholder={t('pages:environments.applyTerraform.sudoPasswordPlaceholder')}
              />
            </div>
          )}

          {/* Channel-grouped image selector */}
          {channelsWithImages.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs text-slate-500">{t('pages:environments.applyTerraform.packerImage', 'Packer Image')}</p>
              <Select value={selectedImageName} onValueChange={setSelectedImageName}>
                <SelectTrigger>
                  <SelectValue placeholder={!(isUpCloud || isGcpVm)
                    ? t('pages:environments.applyTerraform.latestFamily', 'Use image family (latest)')
                    : currentImageName
                      ? t('pages:environments.applyTerraform.keepCurrentImage', 'Keep the current image')
                      : t('pages:environments.applyTerraform.chooseImage', 'Choose an image')} />
                </SelectTrigger>
                <SelectContent>
                  {channelsWithImages.map(channel => (
                    <div key={channel}>
                      <div className="px-2 py-1.5 text-xs font-semibold text-slate-500 flex items-center gap-1.5">
                        <Badge variant={channelVariants[channel]} className="text-[10px] px-1 py-0">
                          {channelLabels[channel]}
                        </Badge>
                      </div>
                      {imageGroups[channel].map(img => (
                        // value is the GCP image name only — this is what
                        // gets sent as the `image_name` tfvar. Including
                        // `/${img.version}` in the value breaks terraform
                        // apply (Error 400: Invalid value for field 'image').
                        <SelectItem key={img.id} value={img.name}>
                          {img.version} <span className="text-slate-400 ml-1">{img.name}</span>
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
              {isUpCloud && selectedImageName && selectedImageName !== currentImageName && (
                <Alert variant="warning">
                  {t(
                    'pages:vmWizard.imageRebuildsUpcloud',
                    'A different base image rebuilds the server. Everything on its local disk is erased, including host credentials and logs. Keep the current image to change only the plan, disk or storage settings.'
                  )}
                </Alert>
              )}
              {isGcpVm && selectedImageName && selectedImageName !== currentImageName && (
                <Alert variant="error">
                  {t(
                    'pages:vmWizard.imageRebuildsGcp',
                    'A different image destroys this virtual machine and its disk, then builds a new one from that image. Everything on the disk is gone: the local database, the host credentials and the logs. Keep the current image to change only the machine type, disk or storage settings.'
                  )}
                </Alert>
              )}
            </div>
          )}

          {/* Advanced settings — GCP, Azure, and UpCloud. Progressive disclosure keeps
              the default path (region + module version + image + SSH key) at
              ~5 fields; provider-specific overrides are reachable in one
              click. k3s has no cloud-placement concept so stays gated. */}
          {!isK3s && environment.cloudProvider === 'gcp' && (
            <AdvancedSettings
              provider="gcp"
              region={environment.region}
              open={advancedOpen}
              onOpenChange={setAdvancedOpen}
              zone={zone}
              onZoneChange={setZone}
              reapply={isGcpVm}
              appliedDiskGb={isGcpVm ? appliedDiskSizeGb(environment) : undefined}
              diskSizeGb={diskSizeGb}
              onDiskSizeChange={setDiskSizeGb}
              dfsEnabled={dfsEnabled}
              onDfsEnabledChange={setDfsEnabled}
              t={t}
            />
          )}
          {!isK3s && environment.cloudProvider === 'azure' && (
            <AzureSshAccess mode={sshAccessMode} sources={sshAllowedCidrs}
              onModeChange={setSshAccessMode} onSourcesChange={setSshAllowedCidrs} />
          )}
          {!isK3s && environment.cloudProvider === 'azure' && (
            <AdvancedSettings
              provider="azure"
              hideAccess
              region={environment.region}
              open={advancedOpen}
              onOpenChange={setAdvancedOpen}
              availabilityZones={availabilityZones}
              onAvailabilityZonesChange={setAvailabilityZones}
              reapply={isAzureVm}
              appliedDiskGb={isAzureVm ? appliedDiskSizeGb(environment) : undefined}
              diskSizeGb={diskSizeGb}
              onDiskSizeChange={setDiskSizeGb}
              dfsEnabled={dfsEnabled}
              onDfsEnabledChange={setDfsEnabled}
              dfsReplication={dfsReplication}
              onDfsReplicationChange={setDfsReplication}
              diagnosticsEnabled={diagnosticsEnabled}
              onDiagnosticsEnabledChange={setDiagnosticsEnabled}
              logRetentionDays={logRetentionDays}
              onLogRetentionDaysChange={setLogRetentionDays}
              sshAllowedCidrs={sshAllowedCidrs}
              onSshAllowedCidrsChange={setSshAllowedCidrs}
              t={t}
            />
          )}
          {!isK3s && environment.cloudProvider === 'upcloud' && (
            <AdvancedSettings
              provider="upcloud"
              region={environment.region}
              open={advancedOpen}
              onOpenChange={setAdvancedOpen}
              reapply
              appliedDiskGb={appliedDiskSizeGb(environment)}
              diskSizeGb={diskSizeGb}
              onDiskSizeChange={setDiskSizeGb}
              dfsEnabled={dfsEnabled}
              onDfsEnabledChange={setDfsEnabled}
              t={t}
            />
          )}

          {error && <p className="text-sm text-red-500">{error}</p>}
        </DialogBody>

        <DialogFooter className="shrink-0 border-t border-slate-200 pt-4 dark:border-slate-800">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            {t('pages:environments.applyTerraform.cancel', 'Cancel')}
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={isSubmitting}>
            {isSubmitting ? (
              <><Spinner className="h-4 w-4 mr-1" /> {t('pages:environments.applyTerraform.applying', 'Applying...')}</>
            ) : (
              <><Play size={14} className="mr-1" /> {t('pages:environments.applyTerraform.apply', 'Apply')}</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
